/**
 * Edit F&O Job Dialog
 *
 * Sheet panel to view and edit properties of an F&O Job:
 * Title, Description, Function, Job Family, FTE, position limits.
 * Backend endpoints: GET /fo-job?job_id=, POST /update-fo-job.
 */

import React, { useState, useEffect } from 'react';
import { Loader2, CheckCircle, AlertTriangle, Settings } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter,
} from '@/components/ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { jobFamilySyncService } from '@/services/api/organizationService';
import roleMasterService from '@/services/api/roleMasterService';

interface EditFoJobDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  jobId: string;
  /** Optional: pre-fill fields from RM entry when the F&O job is sparse */
  defaults?: {
    functional_area?: string;  // Maps to JobFamilyId (not FunctionId)
    description?: string;
    grade_level?: string;      // Maps to CompensationLevelId
  };
}

interface FoJobForm {
  title_id: string;
  description: string;
  job_family_id: string;
  function_id: string;
  full_time_equivalent: number;
  allow_unlimited: boolean;
  maximum_positions: number;
  compensation_level: string;
}

const EditFoJobDialog: React.FC<EditFoJobDialogProps> = ({
  open, onOpenChange, jobId, defaults,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [form, setForm] = useState<FoJobForm>({
    title_id: '',
    description: '',
    job_family_id: '',
    function_id: '',
    full_time_equivalent: 1.0,
    allow_unlimited: true,
    maximum_positions: 0,
    compensation_level: '',
  });
  const [result, setResult] = useState<{ updated: string[]; errors: any[] } | null>(null);
  // Canonical grade list (A1..F4, NG) for the Compensation Level dropdown.
  const [gradeOptions, setGradeOptions] = useState<string[]>([]);

  useEffect(() => {
    if (open && jobId) {
      loadJob();
    }
    // Reset on close
    if (!open) {
      setResult(null);
      setLoadError(null);
    }
  }, [open, jobId]);

  // Load canonical grade options when the dialog opens (non-blocking).
  useEffect(() => {
    if (!open) return;
    roleMasterService.getFilterOptions()
      .then((opts) => setGradeOptions(opts.grade_levels || []))
      .catch((e) => { console.error('Failed to load grade options:', e); });
  }, [open]);

  const loadJob = async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const data = await jobFamilySyncService.getJobDetail(jobId);
      // Prefer the LIVE F&O compensation level from the JobId→level map — the
      // job-detail payload doesn't always carry it. Fall back to the detail
      // value, then to the RM default only if F&O has none. The extra fetch is
      // best-effort and must never block the dialog.
      let foLevel = data.compensation_level_id || data.CompensationLevelId || '';
      try {
        const comps = await jobFamilySyncService.getJobCompensations();
        if (comps && comps[jobId]) foLevel = comps[jobId];
      } catch (compErr) {
        console.error('Failed to load F&O job compensations:', compErr);
      }
      setForm({
        title_id: data.title_id || data.TitleId || '',
        description: data.description || data.Description || defaults?.description || '',
        job_family_id: data.job_family_id || data.JobFamilyId || defaults?.functional_area || '',
        function_id: data.function_id || data.FunctionId || '',
        compensation_level: foLevel || defaults?.grade_level || '',
        full_time_equivalent: data.full_time_equivalent ?? data.FullTimeEquivalent ?? 1.0,
        allow_unlimited: (data.allow_unlimited_positions || data.AllowUnlimitedPositions || 'No') === 'Yes',
        maximum_positions: data.maximum_number_of_positions ?? data.MaximumNumberOfPositions ?? 0,
      });
    } catch (error: any) {
      console.error('Failed to load F&O job:', error);
      setLoadError(error?.response?.data?.detail || 'Failed to load job from F&O');
      // Pre-fill from defaults even if load fails
      setForm(prev => ({
        ...prev,
        title_id: '',
        description: defaults?.description || '',
        job_family_id: defaults?.functional_area || '',
        compensation_level: defaults?.grade_level || '',
      }));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const res = await jobFamilySyncService.updateJob({
        job_id: jobId,
        title_id: form.title_id || undefined,
        description: form.description || undefined,
        job_family_id: form.job_family_id || undefined,
        function_id: form.function_id || undefined,
        full_time_equivalent: form.full_time_equivalent,
        allow_unlimited_positions: form.allow_unlimited ? 'Yes' : 'No',
        maximum_positions: form.allow_unlimited ? 0 : form.maximum_positions,
        compensation_level: form.compensation_level || undefined,
      });
      setResult({ updated: res.updated_fields, errors: res.errors });
    } catch (error: any) {
      console.error('Failed to update job:', error);
      setResult({ updated: [], errors: [{ field: 'general', error: error?.response?.data?.detail || 'Update failed' }] });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5 text-gray-400" />
            Edit F&O Job
          </SheetTitle>
          <SheetDescription className="font-mono">{jobId}</SheetDescription>
        </SheetHeader>

        {isLoading ? (
          <div className="py-12 text-center text-gray-400">
            <Loader2 className="h-6 w-6 animate-spin inline mr-2" />Loading from F&O...
          </div>
        ) : result ? (
          /* Result screen */
          <div className="mt-6 space-y-4">
            {result.updated.length > 0 ? (
              <div className="bg-emerald-50 rounded p-4 text-center">
                <CheckCircle className="h-8 w-8 text-emerald-600 mx-auto mb-2" />
                <p className="font-medium">{result.updated.length} field(s) updated</p>
                <p className="text-sm text-gray-500 mt-1">{result.updated.join(', ')}</p>
              </div>
            ) : (
              <div className="bg-gray-50 rounded p-4 text-center">
                <p className="text-gray-500">No fields were updated</p>
              </div>
            )}
            {result.errors.length > 0 && (
              <div className="bg-red-50 rounded p-3 text-sm text-red-700">
                <p className="font-medium">Errors:</p>
                {result.errors.map((e, i) => (
                  <p key={i}>{e.field}: {e.error}</p>
                ))}
              </div>
            )}
            <SheetFooter>
              <Button onClick={() => onOpenChange(false)}>Done</Button>
            </SheetFooter>
          </div>
        ) : (
          /* Edit form */
          <div className="mt-4 space-y-4">
            {loadError && (
              <div className="bg-amber-50 rounded p-3 text-sm text-amber-700 flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <div>{loadError}. You can still set values below.</div>
              </div>
            )}

            <div>
              <label className="text-sm font-medium text-gray-700">Title ID</label>
              <Input
                value={form.title_id}
                onChange={e => setForm({ ...form, title_id: e.target.value })}
                placeholder="Title linked to this job"
              />
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700">Description</label>
              <Input
                value={form.description}
                onChange={e => setForm({ ...form, description: e.target.value })}
                placeholder="Job description"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Job Family</label>
                <Input
                  value={form.job_family_id}
                  onChange={e => setForm({ ...form, job_family_id: e.target.value })}
                  placeholder="e.g. Academics"
                />
                <p className="text-[10px] text-gray-400 mt-0.5">Must match an F&O Job Family ID</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Function</label>
                <Input
                  value={form.function_id}
                  onChange={e => setForm({ ...form, function_id: e.target.value })}
                  placeholder="e.g. Manage Others"
                />
                <p className="text-[10px] text-gray-400 mt-0.5">Must match an F&O Job Function ID</p>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700">Compensation Level (Grade)</label>
              <Select
                value={form.compensation_level || '__none__'}
                onValueChange={(v) => setForm({ ...form, compensation_level: v === '__none__' ? '' : v })}
              >
                <SelectTrigger><SelectValue placeholder="Select grade" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— None —</SelectItem>
                  {gradeOptions.filter(Boolean).map((gl) => (
                    <SelectItem key={gl} value={gl}>{gl}</SelectItem>
                  ))}
                  {/* Legacy echo: keep the current value selectable even if it's
                      not in the canonical list. */}
                  {form.compensation_level && !gradeOptions.includes(form.compensation_level) && (
                    <SelectItem value={form.compensation_level}>{form.compensation_level}</SelectItem>
                  )}
                </SelectContent>
              </Select>
              <p className="text-[10px] text-gray-400 mt-0.5">Must match an F&O Compensation Level (e.g. B4, C1, C4, D3)</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Full-Time Equivalent</label>
                <Input
                  type="number"
                  step="0.1"
                  min="0"
                  value={form.full_time_equivalent}
                  onChange={e => setForm({ ...form, full_time_equivalent: parseFloat(e.target.value) || 0 })}
                />
              </div>
              <div className="flex flex-col justify-end">
                <div className="flex items-center gap-2 pb-2">
                  <input
                    type="checkbox"
                    id="fo_unlimited"
                    checked={form.allow_unlimited}
                    onChange={e => setForm({ ...form, allow_unlimited: e.target.checked })}
                    className="rounded border-gray-300"
                  />
                  <label htmlFor="fo_unlimited" className="text-sm text-gray-700">Unlimited positions</label>
                </div>
              </div>
            </div>

            {!form.allow_unlimited && (
              <div className="w-1/2">
                <label className="text-sm font-medium text-gray-700">Maximum Positions</label>
                <Input
                  type="number"
                  min="0"
                  value={form.maximum_positions}
                  onChange={e => setForm({ ...form, maximum_positions: parseInt(e.target.value) || 0 })}
                />
              </div>
            )}

            <SheetFooter className="mt-6">
              <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button onClick={handleSave} disabled={isSaving}>
                {isSaving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                Save to F&O
              </Button>
            </SheetFooter>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default EditFoJobDialog;
